import React, { useState } from 'react';
import ContentRenderer, { renderBold } from './ContentRenderer';
import { EvangileItem } from '../../hooks/useEvangile';
import { buildPassageUrl } from '../../utils/url-builder';

interface DetailSheetProps {
  evangile: EvangileItem;
  locale: string;
  onClose: () => void;
  dateLabel: string;
  openLabel: string;
  shareLabel: string;
  nativeShareLabel: string;
}

const DetailSheet: React.FC<DetailSheetProps> = ({
  evangile,
  locale,
  onClose,
  dateLabel,
  openLabel,
  shareLabel,
  nativeShareLabel,
}) => {
  const [videoOverlay, setVideoOverlay] = useState(false);

  const passageUrl = buildPassageUrl(evangile.reference, locale);
  const canonicalUrl = `https://bible4.me/${locale}/evangile/${evangile.date}`;

  // Supprimer la première ligne du contenu si elle est identique à l'accroche (doublon)
  const contenuSansDoublon = (() => {
    const lines = (evangile.contenu || '').split('\n');
    const firstLine = lines[0]?.trim().replace(/^[«""]|[»""]$/g, '').trim();
    const accroche = (evangile.accroche || '').trim().replace(/^[«""]|[»""]$/g, '').trim();
    if (firstLine && accroche && firstLine === accroche) {
      return lines.slice(1).join('\n');
    }
    return evangile.contenu;
  })();
  const whatsappText = encodeURIComponent(`${evangile.accroche}\n\n${canonicalUrl}`);
  const whatsappUrl = `https://wa.me/?text=${whatsappText}`;

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${evangile.reference} — Bible4Me`,
          text: `${evangile.accroche}\n\n${canonicalUrl}`,
        });
      } catch {
        // utilisateur a annulé
      }
    } else {
      // Fallback : copier le lien
      navigator.clipboard?.writeText(`${evangile.accroche}\n\n${canonicalUrl}`);
    }
  };

  // Détection Android pour l'icône
  const isAndroid = /android/i.test(navigator.userAgent);


  return (
    <>
      {/* Overlay vidéo plein écran */}
      {videoOverlay && evangile.videoUrl && (
        <div
          className="fixed inset-0 z-[60] bg-black flex items-center justify-center"
          onClick={() => setVideoOverlay(false)}
        >
          <video
            src={evangile.videoUrl}
            autoPlay
            controls
            playsInline
            className="w-full max-h-full"
            onClick={e => e.stopPropagation()}
            onEnded={() => setVideoOverlay(false)}
          />
          <button
            type="button"
            onClick={() => setVideoOverlay(false)}
            className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/20 flex items-center justify-center"
            aria-label="Fermer la vidéo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      <div
        className="fixed inset-0 z-50 bg-white flex flex-col"
        role="dialog"
        aria-modal="true"
      >
        {/* Header image plein largeur avec gradient + bouton retour + date + référence */}
        <div className="relative flex-shrink-0 bg-white">
          <button
            type="button"
            className="block w-full p-0 m-0 border-none bg-transparent cursor-zoom-in"
            onClick={() => window.open(evangile.imageUrl, '_blank')}
            aria-label="Voir l'image en grand"
          >
            <img src={evangile.imageUrl} alt="" className="w-full h-auto object-contain block pointer-events-none" />
          </button>
          {/* Dégradé supprimé */}

          {/* Boutons retour */}
          <div className="absolute top-4 left-4 flex gap-2">
            {/* Retour à la liste web */}
            <button
              type="button"
              onClick={onClose}
              className="h-9 w-9 rounded-full bg-white/90 flex items-center justify-center shadow"
              aria-label="Retour à la liste"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#451a03" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            {/* Retour à l'app Flutter sur /list. Trois barres, pas deux :
                `bible4me://list` donne un hôte `list` et un chemin vide, que
                le GoRouter ne sait pas placer ; `bible4me:///list` donne le
                chemin `/list`, qu'il connaît. */}
            <a
              href="bible4me:///list"
              className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center shadow border border-amber-300"
              aria-label="Ouvrir dans l'app Bible4Me"
              title="Ouvrir dans l'app Bible4Me"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10" />
                <polyline points="8 12 12 16 16 12" />
                <line x1="12" y1="8" x2="12" y2="16" />
              </svg>
            </a>
          </div>

          {/* Miniature vidéo 9:16 en bas à droite, au-dessus de la bande */}
          {evangile.videoUrl && (
            <button
              type="button"
              onClick={() => setVideoOverlay(true)}
              className="absolute bottom-9 right-3 w-14 aspect-[9/16] rounded-xl overflow-hidden shadow-lg border border-white/30"
              aria-label="Regarder la vidéo"
            >
              <img
                src={evangile.portraitThumbUrl || evangile.imageUrl}
                alt=""
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                <div className="h-8 w-8 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center border border-white/50">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                    <polygon points="6 3 20 12 6 21 6 3" />
                  </svg>
                </div>
              </div>
            </button>
          )}

          {/* Deux chips orange en bas à gauche de l'image */}
          <div className="absolute bottom-3 left-3 flex items-center gap-2">
            <span className="bg-amber-600 text-white text-[0.62rem] font-semibold px-2.5 py-0.5 rounded-full uppercase tracking-wide">
              {dateLabel}
            </span>
            <a
              href={passageUrl}
              target="_blank"
              rel="noreferrer"
              className="bg-amber-600 text-white text-[0.75rem] font-bold px-3 py-0.5 rounded-full hover:bg-amber-500 transition-colors"
            >
              {evangile.reference}
            </a>
          </div>
        </div>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto px-4 py-5 pb-6">
          <p className="font-serif font-bold text-[1.08rem] text-amber-900 leading-relaxed mb-5">
            {renderBold(evangile.accroche, 'accroche')}
          </p>
          <ContentRenderer text={contenuSansDoublon} locale={locale} />
        </div>

        {/* Barre sticky en bas : WhatsApp | Partager | Lire le passage */}
        <div className="flex-shrink-0 border-t border-amber-100 bg-white px-4 py-3 flex flex-col gap-2">
          <div className="flex gap-2">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noreferrer"
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm text-white"
            style={{ backgroundColor: '#25D366' }}
            aria-label={shareLabel}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
              <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.554 4.118 1.522 5.849L.057 23.535a.75.75 0 00.916.919l5.741-1.485A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.886 0-3.65-.51-5.166-1.399l-.371-.22-3.853.997 1.02-3.763-.242-.386A9.944 9.944 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
            </svg>
            {shareLabel}
          </a>

          {/* Bouton partage natif OS */}
          <button
            type="button"
            onClick={handleNativeShare}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm bg-amber-100 text-amber-900"
            aria-label={nativeShareLabel}
          >
            {isAndroid ? (
              /* Android share icon */
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"/>
                <circle cx="6" cy="12" r="3"/>
                <circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
            ) : (
              /* iOS share icon (box + arrow up) */
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/>
                <polyline points="16 6 12 2 8 6"/>
                <line x1="12" y1="2" x2="12" y2="15"/>
              </svg>
            )}
            {nativeShareLabel}
          </button>

          <a
            href={passageUrl}
            target="_blank"
            rel="noreferrer"
            className="flex-1 flex items-center justify-center py-3 rounded-xl font-bold text-sm text-white bg-amber-900 text-center"
          >
            {openLabel} →
          </a>
        </div>
        </div>
      </div>
    </>
  );
};

export default DetailSheet;
